import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Card,
  CardContent,
  Zoom,
  LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Group as GroupIcon,
  GroupAdd as GroupAddIcon,
  PersonAdd as PersonAddIcon,
  LockOpen as LockOpenIcon,
  CheckCircleOutline as CheckCircleIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Sync as SyncIcon,
  Stop as StopIcon,
  CloudDone as CloudDoneIcon,
  Warning as WarningIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoveToInbox as MoveToInboxIcon,
  Build as BuildIcon
} from "@material-ui/icons";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastUtils from "../../utils/toastUtils";
import toastError from "../../errors/toastError";
import AutocompleteMultipleUsers from "../AutocompleteMultipleUsers";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    maxHeight: "92vh",
    width: "100%",
    maxWidth: 1050,
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px 12px",
    borderBottom: "1px solid " + (theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"),
  },
  titleIconBox: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  titleIcon: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: 12,
    padding: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  metricCard: {
    borderRadius: 12,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    border: "2px solid transparent",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
    },
  },
  metricCardActive: {
    borderColor: theme.palette.primary.main + " !important",
    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
    },
  },
  tableContainer: {
    maxHeight: "52vh",
    borderRadius: 10,
    border: "1px solid " + (theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"),
    marginTop: 12,
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: 13,
    backgroundColor: theme.palette.type === "dark" ? "#1e222d" : "#f8f9fa",
  },
  tableRow: {
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
    },
  },
  tableRowSelected: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(63, 81, 181, 0.15) !important" : "rgba(63, 81, 181, 0.08) !important",
  },
  groupAvatar: {
    width: 40,
    height: 40,
    borderRadius: 10,
  },
  bulkBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    padding: "12px 20px",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#1e2433" : "#212529",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    marginTop: 12,
  },
  userChip: {
    margin: "2px 4px 2px 0",
    fontSize: 12,
    fontWeight: 500,
    height: 26,
  },
  assignDialogPaper: {
    borderRadius: 14,
    width: "100%",
    maxWidth: 520,
  },
}));

const EMPTY_INITIAL_IDS = [];

const GroupPermissionsModal = ({ open, onClose, initialSelectedGroupIds = EMPTY_INITIAL_IDS }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ totalGroups: 0, publicGroups: 0, restrictedGroups: 0 });
  const [searchParam, setSearchParam] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'public' | 'restricted'
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // Sincronização detalhada de Grupos do WhatsApp
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncStep, setSyncStep] = useState("initial"); // 'initial' | 'syncing' | 'completed' | 'cancelled' | 'error'
  const [syncStatus, setSyncStatus] = useState("");
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState("");
  const [stoppingSync, setStoppingSync] = useState(false);

  // Modal de atribuição em massa
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignMode, setAssignMode] = useState("replace"); // 'replace' | 'add'
  const [targetContactIds, setTargetContactIds] = useState([]);

  // Modal de confirmação para tornar público
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // Modais de confirmação para exclusão de grupos
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Normalização de grupos (mover para a Aba Grupos)
  const [normalizing, setNormalizing] = useState(false);
  const [normalizeConfirmOpen, setNormalizeConfirmOpen] = useState(false);

  // Limpeza de grupos e tickets duplicados (produção)
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);

  // Buscar grupos e permissões
  const fetchGroupPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/groups/permissions");
      setGroups(data.groups || []);
      setStats(data.stats || { totalGroups: 0, publicGroups: 0, restrictedGroups: 0, outOfGroupTabCount: 0 });
    } catch (err) {
      console.error("Erro ao carregar permissões de grupos:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Normalizar todos os grupos (Mover todos para a Aba Grupos)
  const handleNormalizeAll = async () => {
    setNormalizing(true);
    setNormalizeConfirmOpen(false);
    try {
      const { data } = await api.post("/contacts/groups/normalize");
      toastUtils.success(data.message || "Grupos normalizados e movidos para a Aba Grupos com sucesso!");
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao normalizar grupos:", err);
      toastError(err);
    } finally {
      setNormalizing(false);
    }
  };

  // Normalizar grupo único
  const handleNormalizeSingleGroup = async (contactId) => {
    setNormalizing(true);
    try {
      const { data } = await api.post("/contacts/groups/normalize", { contactId });
      toastUtils.success(data.message || "Grupo movido para a Aba Grupos com sucesso!");
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao normalizar grupo:", err);
      toastError(err);
    } finally {
      setNormalizing(false);
    }
  };

  // Limpar contatos e tickets duplicados de grupos
  const handleCleanupDuplicates = async () => {
    setCleaningDuplicates(true);
    setCleanupConfirmOpen(false);
    try {
      const { data } = await api.post("/contacts/groups/cleanup-duplicates");
      toastUtils.success(
        data.message ||
          `Limpeza concluída! ${data.mergedContactsCount || 0} contatos mesclados e ${data.closedTicketsCount || 0} tickets duplicados fechados.`
      );
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao limpar grupos duplicados:", err);
      toastError(err);
    } finally {
      setCleaningDuplicates(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchGroupPermissions();
      if (Array.isArray(initialSelectedGroupIds) && initialSelectedGroupIds.length > 0) {
        setSelectedGroupIds(initialSelectedGroupIds);
      } else {
        setSelectedGroupIds([]);
      }
    } else {
      setSelectedGroupIds([]);
      setSearchParam("");
    }
  }, [open]);

  // Escutar eventos Socket em tempo real de sincronização de grupos
  useEffect(() => {
    if (!socket || !open) return;

    const handleSyncGroupsEvent = (data) => {
      console.log("[GroupPermissionsModal] Evento sync-groups recebido:", data);
      if (!data || !data.action) return;

      const { action, status, result, error, progress, userId } = data;
      if (userId && user?.id && Number(userId) !== Number(user.id)) return;

      switch (action) {
        case "start":
          setSyncStep("syncing");
          setSyncStatus(status || "Iniciando sincronização...");
          setSyncProgress({ current: 0, total: 0 });
          setStoppingSync(false);
          setSyncModalOpen(true);
          break;

        case "progress":
          setSyncStep("syncing");
          if (status) setSyncStatus(status);
          if (progress && progress.total !== undefined) {
            setSyncProgress({
              current: progress.current || 0,
              total: progress.total || 0,
            });
          }
          break;

        case "complete":
          setSyncStep("completed");
          setSyncStatus(status || "Sincronização concluída com sucesso!");
          setSyncResult(result || null);
          setStoppingSync(false);
          toastUtils.success("Sincronização de grupos concluída!");
          fetchGroupPermissions();
          break;

        case "cancelled":
          setSyncStep("cancelled");
          setSyncStatus(status || "Sincronização cancelada.");
          setStoppingSync(false);
          toastUtils.warning("Sincronização interrompida.");
          fetchGroupPermissions();
          break;

        case "error":
          setSyncStep("error");
          setSyncStatus("Erro na sincronização de grupos.");
          setSyncError(error || "Erro desconhecido");
          setStoppingSync(false);
          break;

        default:
          break;
      }
    };

    socket.on("sync-groups", handleSyncGroupsEvent);

    return () => {
      socket.off("sync-groups", handleSyncGroupsEvent);
    };
  }, [socket, open, user?.id, fetchGroupPermissions]);

  // Iniciar Sincronização de Grupos
  const handleStartSync = async () => {
    try {
      setSyncStep("syncing");
      setSyncStatus("Conectando ao WhatsApp e obtendo lista de grupos...");
      setSyncProgress({ current: 0, total: 0 });
      setSyncResult(null);
      setSyncError("");
      setStoppingSync(false);
      setSyncModalOpen(true);

      await api.post("/groups/sync");
    } catch (err) {
      console.error("Erro ao iniciar sincronização:", err);
      setSyncStep("error");
      setSyncError(err?.response?.data?.message || err.message || "Erro ao iniciar sincronização");
    }
  };

  // Parar/Cancelar Sincronização de Grupos
  const handleStopSync = async () => {
    setStoppingSync(true);
    try {
      setSyncStatus("Interrompendo sincronização no servidor...");
      await api.post("/groups/sync/cancel");
      toastUtils.info("Interrupção da sincronização solicitada...");
    } catch (err) {
      console.error("Erro ao cancelar sincronização:", err);
      toastError(err);
      setStoppingSync(false);
    }
  };

  // Continuar em segundo plano
  const handleContinueInBackground = () => {
    setSyncModalOpen(false);
    toastUtils.info(
      `Sincronização em andamento em segundo plano (${syncProgress.current} de ${syncProgress.total || "?"} grupos). A lista atualizará ao concluir.`
    );
  };

  // Fechar modal de sincronização e atualizar dados
  const handleCloseSyncModal = () => {
    setSyncModalOpen(false);
    setSyncStep("initial");
    fetchGroupPermissions();
  };

  // Filtragem dos grupos exibidos
  const filteredGroups = useMemo(() => {
    return groups.filter((g) => {
      const matchesSearch =
        searchParam.trim() === "" ||
        g.name.toLowerCase().includes(searchParam.toLowerCase()) ||
        g.users.some((u) => u.name.toLowerCase().includes(searchParam.toLowerCase()));

      if (!matchesSearch) return false;

      if (activeFilter === "public") return g.isPublic;
      if (activeFilter === "restricted") return !g.isPublic;
      if (activeFilter === "outOfGroupTab") return g.isOutOfGroupTab;
      return true;
    });
  }, [groups, searchParam, activeFilter]);

  // Checkbox Selecionar Todos
  const allFilteredSelected =
    filteredGroups.length > 0 &&
    filteredGroups.every((g) => selectedGroupIds.includes(g.id));
  const someFilteredSelected =
    filteredGroups.some((g) => selectedGroupIds.includes(g.id)) &&
    !allFilteredSelected;

  const handleToggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = filteredGroups.map((g) => g.id);
      setSelectedGroupIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      const filteredIds = filteredGroups.map((g) => g.id);
      setSelectedGroupIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleToggleSelectGroup = (contactId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  // Abrir modal para atribuir usuários
  const handleOpenAssignModal = (contactIds = []) => {
    const ids = contactIds.length > 0 ? contactIds : selectedGroupIds;
    if (ids.length === 0) {
      toastUtils.warn("Selecione pelo menos um grupo");
      return;
    }
    setTargetContactIds(ids);
    setSelectedUsers([]);
    setAssignMode("replace");
    setAssignModalOpen(true);
  };

  // Abrir modal de atribuição para 1 único grupo (edição inline)
  const handleEditSingleGroup = (group) => {
    setTargetContactIds([group.id]);
    setSelectedUsers(group.users.map((u) => ({ id: u.id, name: u.name })));
    setAssignMode("replace");
    setAssignModalOpen(true);
  };

  // Executar salvamento em massa
  const handleSaveBatchPermissions = async () => {
    if (targetContactIds.length === 0) return;
    setSaving(true);
    try {
      const userIds = selectedUsers.map((u) => u.id);
      await api.post("/contacts/groups/batch-permissions", {
        contactIds: targetContactIds,
        userIds,
        mode: assignMode,
      });

      toastUtils.success(
        targetContactIds.length > 1
          ? `Permissões atualizadas para ${targetContactIds.length} grupos com sucesso!`
          : "Permissões do grupo atualizadas com sucesso!"
      );
      setAssignModalOpen(false);
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao salvar permissões:", err);
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Executar "Tornar Público" (mode: 'clear')
  const handleConfirmClearPermissions = async () => {
    if (selectedGroupIds.length === 0) return;
    setSaving(true);
    try {
      await api.post("/contacts/groups/batch-permissions", {
        contactIds: selectedGroupIds,
        mode: "clear",
      });

      toastUtils.success(
        `${selectedGroupIds.length} grupo(s) tornados públicos com sucesso! Todos os atendentes agora podem vê-los.`
      );
      setClearConfirmOpen(false);
      setSelectedGroupIds([]);
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao limpar permissões:", err);
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Remover 1 único usuário do grupo diretamente com clique no 'x' do chip
  const handleRemoveSingleUserFromGroup = async (group, userToRemove) => {
    const updatedUsers = group.users.filter((u) => u.id !== userToRemove.id);
    try {
      await api.post("/contacts/groups/batch-permissions", {
        contactIds: [group.id],
        userIds: updatedUsers.map((u) => u.id),
        mode: "replace",
      });
      toastUtils.success(`${userToRemove.name} removido do grupo ${group.name}`);
      await fetchGroupPermissions();
    } catch (err) {
      toastError(err);
    }
  };

  // Abrir modal de exclusão de grupo individual
  const handleOpenDeleteGroup = (group) => {
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
  };

  // Confirmar exclusão de grupo individual
  const handleConfirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/contacts/groups/${groupToDelete.id}`);
      toastUtils.success(`Grupo "${groupToDelete.name}" excluído com sucesso!`);
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
      setSelectedGroupIds((prev) => prev.filter((id) => id !== groupToDelete.id));
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao excluir grupo:", err);
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  // Confirmar exclusão de múltiplos grupos
  const handleConfirmBulkDeleteGroups = async () => {
    if (selectedGroupIds.length === 0) return;
    setDeleting(true);
    try {
      await api.post("/contacts/bulk-delete", {
        contactIds: selectedGroupIds,
      });
      toastUtils.success(`${selectedGroupIds.length} grupo(s) excluído(s) com sucesso!`);
      setBulkDeleteConfirmOpen(false);
      setSelectedGroupIds([]);
      await fetchGroupPermissions();
    } catch (err) {
      console.error("Erro ao excluir grupos selecionados:", err);
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <div className={classes.titleIconBox}>
            <div className={classes.titleIcon}>
              <GroupAddIcon fontSize="medium" />
            </div>
            <div>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Central de Permissões de Grupos
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Defina visualmente quais atendentes podem ver e responder em cada grupo do WhatsApp
              </Typography>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tooltip title="Mover todos os grupos que estejam em filas (Aguardando ou Atendendo) de volta para a Aba Grupos">
              <span>
                <Button
                  variant={stats.outOfGroupTabCount > 0 ? "contained" : "outlined"}
                  size="small"
                  disabled={stoppingSync || normalizing}
                  onClick={() => setNormalizeConfirmOpen(true)}
                  startIcon={normalizing ? <CircularProgress size={16} color="inherit" /> : <MoveToInboxIcon />}
                  style={{
                    borderRadius: 8,
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    backgroundColor: stats.outOfGroupTabCount > 0 ? "#f57c00" : undefined,
                    color: stats.outOfGroupTabCount > 0 ? "#fff" : "#f57c00",
                    borderColor: "#f57c00"
                  }}
                >
                  {normalizing
                    ? "Normalizando..."
                    : stats.outOfGroupTabCount > 0
                    ? `Mover para Aba Grupos (${stats.outOfGroupTabCount})`
                    : "Mover para Aba Grupos"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Limpar contatos e tickets duplicados de grupos, consolidando históricos e garantindo apenas 1 ticket por grupo">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={stoppingSync || normalizing || cleaningDuplicates}
                  onClick={() => setCleanupConfirmOpen(true)}
                  startIcon={cleaningDuplicates ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
                  style={{
                    borderRadius: 8,
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    borderColor: "#673ab7",
                    color: "#673ab7"
                  }}
                >
                  {cleaningDuplicates ? "Corrigindo..." : "Corrigir Duplicados"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Buscar e importar todos os grupos do WhatsApp conectado que ainda não foram importados">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={stoppingSync}
                  onClick={() => {
                    if (syncStep === "syncing") {
                      setSyncModalOpen(true);
                    } else {
                      handleStartSync();
                    }
                  }}
                  startIcon={syncStep === "syncing" ? <CircularProgress size={16} /> : <SyncIcon />}
                  style={{ borderRadius: 8, textTransform: "none", fontSize: 12, fontWeight: 600 }}
                >
                  {syncStep === "syncing"
                    ? syncProgress.total > 0
                      ? `Sincronizando (${syncProgress.current}/${syncProgress.total})`
                      : "Sincronizando..."
                    : "Sincronizar Grupos do WhatsApp"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Atualizar dados">
              <span>
                <IconButton size="small" onClick={fetchGroupPermissions} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: "16px 24px" }}>
          {/* CARDS DE MÉTRICAS */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={3}>
              <Card
                variant="outlined"
                className={`${classes.metricCard} ${activeFilter === "all" ? classes.metricCardActive : ""}`}
                onClick={() => setActiveFilter("all")}
              >
                <CardContent style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                       TOTAL DE GRUPOS
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800, color: "#1976d2" }}>
                      {stats.totalGroups}
                    </Typography>
                  </div>
                  <Avatar style={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}>
                    <GroupIcon />
                  </Avatar>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Card
                variant="outlined"
                className={`${classes.metricCard} ${activeFilter === "public" ? classes.metricCardActive : ""}`}
                onClick={() => setActiveFilter("public")}
              >
                <CardContent style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                      PÚBLICOS (TODOS PODEM VER)
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800, color: "#2e7d32" }}>
                      {stats.publicGroups}
                    </Typography>
                  </div>
                  <Avatar style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                    <PublicIcon />
                  </Avatar>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Card
                variant="outlined"
                className={`${classes.metricCard} ${activeFilter === "restricted" ? classes.metricCardActive : ""}`}
                onClick={() => setActiveFilter("restricted")}
              >
                <CardContent style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                      RESTRITOS (ACESSO CONTROLADO)
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800, color: "#e65100" }}>
                      {stats.restrictedGroups}
                    </Typography>
                  </div>
                  <Avatar style={{ backgroundColor: "#fff3e0", color: "#e65100" }}>
                    <LockIcon />
                  </Avatar>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Card
                variant="outlined"
                className={`${classes.metricCard} ${activeFilter === "outOfGroupTab" ? classes.metricCardActive : ""}`}
                onClick={() => setActiveFilter("outOfGroupTab")}
                style={{
                  borderColor: stats.outOfGroupTabCount > 0 ? "#f57c00" : undefined,
                  backgroundColor: stats.outOfGroupTabCount > 0 ? "#fff8e1" : undefined
                }}
              >
                <CardContent style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                      FORA DA ABA GRUPOS
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800, color: stats.outOfGroupTabCount > 0 ? "#e65100" : "#757575" }}>
                      {stats.outOfGroupTabCount || 0}
                    </Typography>
                  </div>
                  <Avatar style={{ backgroundColor: stats.outOfGroupTabCount > 0 ? "#ffe0b2" : "#eeeeee", color: stats.outOfGroupTabCount > 0 ? "#e65100" : "#757575" }}>
                    <WarningIcon />
                  </Avatar>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* BARRA DE BUSCA E FILTROS */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Pesquisar por nome do grupo ou atendente..."
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" style={{ color: "#888" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchParam ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchParam("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <Button
                variant={activeFilter === "all" ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => setActiveFilter("all")}
                style={{ borderRadius: 8, textTransform: "none", fontSize: 12 }}
              >
                Todos ({stats.totalGroups})
              </Button>
              <Button
                variant={activeFilter === "public" ? "contained" : "outlined"}
                size="small"
                onClick={() => setActiveFilter("public")}
                style={{
                  borderRadius: 8,
                  textTransform: "none",
                  fontSize: 12,
                  backgroundColor: activeFilter === "public" ? "#2e7d32" : undefined,
                  color: activeFilter === "public" ? "#fff" : "#2e7d32",
                  borderColor: "#2e7d32",
                }}
              >
                Públicos ({stats.publicGroups})
              </Button>
              <Button
                variant={activeFilter === "restricted" ? "contained" : "outlined"}
                size="small"
                onClick={() => setActiveFilter("restricted")}
                style={{
                  borderRadius: 8,
                  textTransform: "none",
                  fontSize: 12,
                  backgroundColor: activeFilter === "restricted" ? "#e65100" : undefined,
                  color: activeFilter === "restricted" ? "#fff" : "#e65100",
                  borderColor: "#e65100",
                }}
              >
                Restritos ({stats.restrictedGroups})
              </Button>
              <Button
                variant={activeFilter === "outOfGroupTab" ? "contained" : "outlined"}
                size="small"
                onClick={() => setActiveFilter("outOfGroupTab")}
                style={{
                  borderRadius: 8,
                  textTransform: "none",
                  fontSize: 12,
                  backgroundColor: activeFilter === "outOfGroupTab" ? "#e65100" : undefined,
                  color: activeFilter === "outOfGroupTab" ? "#fff" : (stats.outOfGroupTabCount > 0 ? "#e65100" : undefined),
                  borderColor: stats.outOfGroupTabCount > 0 ? "#e65100" : undefined,
                  fontWeight: stats.outOfGroupTabCount > 0 ? 700 : 400
                }}
              >
                Fora da Aba Grupos ({stats.outOfGroupTabCount || 0})
              </Button>
            </Grid>
          </Grid>

          {/* TABELA DE GRUPOS */}
          <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" className={classes.tableHeaderCell}>
                    <Checkbox
                      color="primary"
                      indeterminate={someFilteredSelected}
                      checked={allFilteredSelected}
                      onChange={handleToggleSelectAll}
                    />
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} style={{ minWidth: 220 }}>
                    Grupo
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} style={{ width: 160 }}>
                    Visibilidade
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} style={{ minWidth: 280 }}>
                    Atendentes com Acesso
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 120 }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Carregando grupos e permissões...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        Nenhum grupo encontrado
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Tente ajustar os termos de pesquisa ou o filtro selecionado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGroups.map((group) => {
                    const isSelected = selectedGroupIds.includes(group.id);
                    return (
                      <TableRow
                        key={group.id}
                        className={`${classes.tableRow} ${isSelected ? classes.tableRowSelected : ""}`}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isSelected}
                            onChange={() => handleToggleSelectGroup(group.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              src={group.profilePicUrl || group.urlPicture}
                              className={classes.groupAvatar}
                            >
                              <GroupIcon />
                            </Avatar>
                            <div>
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {group.name}
                              </Typography>
                              <Box display="flex" alignItems="center" style={{ marginTop: 2, gap: 6 }}>
                                <Typography variant="caption" color="textSecondary">
                                  ID: #{group.id} {group.unreadMessages > 0 ? `• ${group.unreadMessages} não lidas` : ""}
                                </Typography>
                                {group.isOutOfGroupTab ? (
                                  <Chip
                                    size="small"
                                    label={group.ticketStatus === "pending" ? "Em Aguardando (Fila)" : "Em Atendimento"}
                                    style={{
                                      backgroundColor: group.ticketStatus === "pending" ? "#fff3e0" : "#e1f5fe",
                                      color: group.ticketStatus === "pending" ? "#e65100" : "#0288d1",
                                      fontWeight: 700,
                                      fontSize: 10,
                                      height: 18,
                                      padding: "0 2px"
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Aba Grupos"
                                    style={{
                                      backgroundColor: "#e8f5e9",
                                      color: "#2e7d32",
                                      fontWeight: 600,
                                      fontSize: 10,
                                      height: 18,
                                      padding: "0 2px"
                                    }}
                                  />
                                )}
                              </Box>
                            </div>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {group.isPublic ? (
                            <Chip
                              icon={<PublicIcon style={{ fontSize: 16 }} />}
                              label="Público (Todos)"
                              size="small"
                              style={{
                                backgroundColor: "#e8f5e9",
                                color: "#2e7d32",
                                fontWeight: 600,
                                fontSize: 11,
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<LockIcon style={{ fontSize: 16 }} />}
                              label={`Restrito (${group.userCount})`}
                              size="small"
                              style={{
                                backgroundColor: "#e3f2fd",
                                color: "#1565c0",
                                fontWeight: 600,
                                fontSize: 11,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {group.isPublic ? (
                            <Typography variant="caption" style={{ color: "#777", fontStyle: "italic" }}>
                              Nenhum atendente específico (todos da empresa podem ver)
                            </Typography>
                          ) : (
                            <Box display="flex" flexWrap="wrap" alignItems="center">
                              {group.users.map((u) => (
                                <Tooltip key={u.id} title="Clique no 'X' para remover o acesso deste atendente">
                                  <Chip
                                    label={u.name}
                                    size="small"
                                    onDelete={() => handleRemoveSingleUserFromGroup(group, u)}
                                    className={classes.userChip}
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
                            {group.isOutOfGroupTab && (
                              <Tooltip title="Mover este grupo para a Aba Grupos agora">
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleNormalizeSingleGroup(group.id)}
                                    disabled={normalizing}
                                    style={{
                                      borderRadius: 8,
                                      textTransform: "none",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: "2px 8px",
                                      backgroundColor: "#f57c00",
                                      color: "#fff"
                                    }}
                                  >
                                    Mover para Grupos
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => handleEditSingleGroup(group)}
                              style={{ borderRadius: 8, textTransform: "none", fontSize: 12, padding: "2px 8px" }}
                            >
                              Editar
                            </Button>
                            <Tooltip title="Excluir grupo do sistema (útil para duplicados)">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenDeleteGroup(group)}
                                style={{
                                  borderRadius: 8,
                                  textTransform: "none",
                                  fontSize: 12,
                                  padding: "2px 8px",
                                  color: "#d32f2f",
                                  borderColor: "#ef9a9a",
                                }}
                                startIcon={<DeleteOutlineIcon style={{ fontSize: 16 }} />}
                              >
                                Excluir
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* BARRA DE AÇÕES EM MASSA FLUTUANTE */}
          <Zoom in={selectedGroupIds.length > 0}>
            <div className={classes.bulkBar}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <CheckCircleIcon style={{ color: "#4caf50" }} />
                <Typography variant="body2" style={{ fontWeight: 700 }}>
                  {selectedGroupIds.length} grupo(s) selecionado(s)
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => handleOpenAssignModal()}
                  style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
                >
                  Atribuir Atendentes
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  onClick={() => setClearConfirmOpen(true)}
                  style={{
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 8,
                  }}
                >
                  Tornar Públicos (Livre)
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setBulkDeleteConfirmOpen(true)}
                  style={{
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 8,
                  }}
                >
                  Excluir Selecionados
                </Button>

                <IconButton
                  size="small"
                  onClick={() => setSelectedGroupIds([])}
                  style={{ color: "#aaa" }}
                  title="Limpar seleção"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </div>
          </Zoom>
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
          <Button onClick={onClose} color="default" style={{ textTransform: "none" }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE ATRIBUIÇÃO DE USUÁRIOS */}
      <Dialog
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        classes={{ paper: classes.assignDialogPaper }}
      >
        <DialogTitle disableTypography style={{ padding: "16px 20px 8px", fontWeight: 700 }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {targetContactIds.length > 1
              ? `Atribuir Atendentes a ${targetContactIds.length} Grupos`
              : "Definir Atendentes do Grupo"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Apenas os atendentes selecionados poderão visualizar e interagir com as mensagens destes grupos.
          </Typography>
        </DialogTitle>

        <DialogContent style={{ padding: "12px 20px" }}>
          {targetContactIds.length > 1 && (
            <FormControl component="fieldset" style={{ marginBottom: 16 }}>
              <FormLabel component="legend" style={{ fontSize: 12, fontWeight: 600 }}>
                Modo de Aplicação:
              </FormLabel>
              <RadioGroup
                row
                value={assignMode}
                onChange={(e) => setAssignMode(e.target.value)}
              >
                <FormControlLabel
                  value="replace"
                  control={<Radio color="primary" size="small" />}
                  label={<Typography variant="body2">Substituir lista existente</Typography>}
                />
                <FormControlLabel
                  value="add"
                  control={<Radio color="primary" size="small" />}
                  label={<Typography variant="body2">Adicionar aos já existentes</Typography>}
                />
              </RadioGroup>
            </FormControl>
          )}

          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
            Selecione os Atendentes:
          </Typography>
          <AutocompleteMultipleUsers
            selectedUsers={selectedUsers}
            onUsersChange={(newUsers) => setSelectedUsers(newUsers)}
          />
        </DialogContent>

        <DialogActions style={{ padding: "12px 20px" }}>
          <Button onClick={() => setAssignModalOpen(false)} color="default" disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveBatchPermissions}
            disabled={saving}
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
          >
            {saving ? "Salvando..." : "Salvar Permissões"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE CONFIRMAÇÃO PARA TORNAR PÚBLICO */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: 700 }}>Tornar Grupos Públicos?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja remover as restrições de <b>{selectedGroupIds.length} grupo(s)</b>?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Ao tornar público, <b>todos os atendentes</b> da empresa com acesso a grupos poderão ver e responder a esses grupos.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button onClick={() => setClearConfirmOpen(false)} color="default" disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmClearPermissions}
            disabled={saving}
            style={{ backgroundColor: "#2e7d32", color: "#fff", textTransform: "none", fontWeight: 600 }}
          >
            {saving ? "Processando..." : "Sim, Tornar Públicos"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE PROGRESSO DA SINCRONIZAÇÃO */}
      <Dialog
        open={syncModalOpen}
        onClose={syncStep === "syncing" ? handleContinueInBackground : handleCloseSyncModal}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.assignDialogPaper }}
      >
        <DialogTitle disableTypography style={{ padding: "16px 24px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar style={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}>
                <SyncIcon />
              </Avatar>
              <div>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  Sincronização de Grupos
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Importando grupos da conexão ativa do WhatsApp
                </Typography>
              </div>
            </Box>
            <IconButton
              size="small"
              onClick={syncStep === "syncing" ? handleContinueInBackground : handleCloseSyncModal}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: "24px" }}>
          {syncStep === "syncing" && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <CircularProgress size={26} />
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {syncStatus}
                </Typography>
              </Box>

              {syncProgress.total > 0 ? (
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                      Progresso: {syncProgress.current} de {syncProgress.total} grupos
                    </Typography>
                    <Chip
                      label={`${Math.round((syncProgress.current / syncProgress.total) * 100)}%`}
                      size="small"
                      color="primary"
                      style={{ fontWeight: 700 }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((syncProgress.current / syncProgress.total) * 100)}
                    style={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
              ) : (
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                    Aguardando resposta do WhatsApp...
                  </Typography>
                  <LinearProgress style={{ height: 10, borderRadius: 5 }} />
                </Box>
              )}

              <Box mt={3} p={1.5} style={{ backgroundColor: "rgba(25, 118, 210, 0.06)", borderRadius: 8 }}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  <span role="img" aria-label="dica">💡</span> <strong>Segurança do WhatsApp:</strong> Os grupos são processados em pequenos lotes com intervalos inteligentes para evitar sobrecarga na conexão e proteger seu chip.
                </Typography>
              </Box>
            </Box>
          )}

          {syncStep === "completed" && (
            <Box textAlign="center" py={2}>
              <Avatar style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", width: 64, height: 64, margin: "0 auto 16px" }}>
                <CloudDoneIcon style={{ fontSize: 36 }} />
              </Avatar>
              <Typography variant="h6" style={{ fontWeight: 700, color: "#2e7d32", marginBottom: 8 }}>
                Sincronização Concluída!
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                {syncStatus}
              </Typography>

              {syncResult && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" style={{ padding: 12, borderRadius: 10, textAlign: "center" }}>
                      <Typography variant="h5" style={{ fontWeight: 800, color: "#1976d2" }}>
                        {syncResult.totalGroups || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Total Encontrados
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" style={{ padding: 12, borderRadius: 10, textAlign: "center" }}>
                      <Typography variant="h5" style={{ fontWeight: 800, color: "#2e7d32" }}>
                        {syncResult.newGroups || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Novos Importados
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {syncStep === "cancelled" && (
            <Box textAlign="center" py={2}>
              <Avatar style={{ backgroundColor: "#fff3e0", color: "#e65100", width: 64, height: 64, margin: "0 auto 16px" }}>
                <WarningIcon style={{ fontSize: 36 }} />
              </Avatar>
              <Typography variant="h6" style={{ fontWeight: 700, color: "#e65100", marginBottom: 8 }}>
                Sincronização Interrompida
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                {syncStatus}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Todos os grupos processados até a parada ({syncProgress.current} de {syncProgress.total}) foram salvos com segurança e já estão disponíveis.
              </Typography>
            </Box>
          )}

          {syncStep === "error" && (
            <Box textAlign="center" py={2}>
              <Avatar style={{ backgroundColor: "#ffebee", color: "#d32f2f", width: 64, height: 64, margin: "0 auto 16px" }}>
                <WarningIcon style={{ fontSize: 36 }} />
              </Avatar>
              <Typography variant="h6" style={{ fontWeight: 700, color: "#d32f2f", marginBottom: 8 }}>
                Falha na Sincronização
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {syncError || syncStatus}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
          {syncStep === "syncing" && (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleStopSync}
                disabled={stoppingSync}
                startIcon={stoppingSync ? <CircularProgress size={16} /> : <StopIcon />}
                style={{ borderRadius: 8, textTransform: "none", color: "#d32f2f", borderColor: "#d32f2f" }}
              >
                {stoppingSync ? "Parando..." : "Parar Sincronização"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinueInBackground}
                style={{ borderRadius: 8, textTransform: "none" }}
              >
                Continuar em Segundo Plano
              </Button>
            </>
          )}

          {(syncStep === "completed" || syncStep === "cancelled") && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseSyncModal}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              Concluir e Ver Grupos
            </Button>
          )}

          {syncStep === "error" && (
            <>
              <Button
                variant="outlined"
                onClick={() => setSyncModalOpen(false)}
                style={{ borderRadius: 8, textTransform: "none" }}
              >
                Fechar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartSync}
                startIcon={<SyncIcon />}
                style={{ borderRadius: 8, textTransform: "none" }}
              >
                Tentar Novamente
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO INDIVIDUAL */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10, color: "#d32f2f" }}>
          <WarningIcon style={{ color: "#d32f2f" }} />
          Excluir Grupo do Sistema
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            Deseja realmente excluir o grupo <strong>{groupToDelete?.name}</strong> (ID: #{groupToDelete?.id})?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta ação removerá o registro do grupo, suas permissões e os atendimentos vinculados a ele. É a função ideal para eliminar grupos duplicados ou desnecessários.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            color="default"
            disabled={deleting}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDeleteGroup}
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff", textTransform: "none", borderRadius: 8 }}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {deleting ? "Excluindo..." : "Sim, Excluir Grupo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO EM MASSA */}
      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => !deleting && setBulkDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10, color: "#d32f2f" }}>
          <WarningIcon style={{ color: "#d32f2f" }} />
          Excluir Grupos Selecionados
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            Deseja realmente excluir os <strong>{selectedGroupIds.length}</strong> grupo(s) selecionado(s) do sistema?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Todos os atendimentos, mensagens e permissões vinculados a estes grupos serão excluídos permanentemente.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setBulkDeleteConfirmOpen(false)}
            color="default"
            disabled={deleting}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmBulkDeleteGroups}
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff", textTransform: "none", borderRadius: 8 }}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {deleting ? "Excluindo..." : `Sim, Excluir (${selectedGroupIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE CONFIRMAÇÃO PARA MOVER GRUPOS PARA A ABA GRUPOS */}
      <Dialog
        open={normalizeConfirmOpen}
        onClose={() => !normalizing && setNormalizeConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10, color: "#f57c00" }}>
          <MoveToInboxIcon style={{ color: "#f57c00" }} />
          Mover Grupos para a Aba Grupos
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            Deseja reorganizar todos os grupos para que fiquem exclusivamente na <strong>Aba Grupos</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {stats.outOfGroupTabCount > 0
              ? `Existem atualmente ${stats.outOfGroupTabCount} grupo(s) em filas de 'Aguardando' ou 'Atendendo'. Ao confirmar, todos eles terão o status normalizado para 'group', removendo filas e atendentes indevidos.`
              : "Todos os grupos cadastrados serão verificados e seus atendimentos vinculados serão normalizados para a aba de grupos."}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setNormalizeConfirmOpen(false)}
            color="default"
            disabled={normalizing}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleNormalizeAll}
            variant="contained"
            style={{ backgroundColor: "#f57c00", color: "#fff", textTransform: "none", borderRadius: 8 }}
            disabled={normalizing}
            startIcon={normalizing ? <CircularProgress size={16} color="inherit" /> : <MoveToInboxIcon />}
          >
            {normalizing ? "Normalizando..." : "Sim, Mover para Grupos"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE CONFIRMAÇÃO PARA LIMPAR DUPLICADOS */}
      <Dialog
        open={cleanupConfirmOpen}
        onClose={() => !cleaningDuplicates && setCleanupConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10, color: "#673ab7" }}>
          <BuildIcon style={{ color: "#673ab7" }} />
          Corrigir Grupos e Tickets Duplicados
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            Deseja executar a correção e unificação de grupos duplicados?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Esta ferramenta vai:
          </Typography>
          <Typography variant="body2" color="textSecondary" component="div">
            <ul>
              <li>Unificar cadastros duplicados do mesmo grupo no banco de dados.</li>
              <li>Fechar tickets duplicados e históricos antigos abertos por engano.</li>
              <li>Manter exatamente <strong>1 ticket ativo</strong> por grupo com todas as mensagens preservadas.</li>
              <li>Limpar sufixos indevidos (ex: <code>@g.us</code>) nos números dos contatos.</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setCleanupConfirmOpen(false)}
            color="default"
            disabled={cleaningDuplicates}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCleanupDuplicates}
            variant="contained"
            style={{ backgroundColor: "#673ab7", color: "#fff", textTransform: "none", borderRadius: 8 }}
            disabled={cleaningDuplicates}
            startIcon={cleaningDuplicates ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
          >
            {cleaningDuplicates ? "Corrigindo..." : "Sim, Corrigir Duplicados"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupPermissionsModal;
